import { FormData } from "../../core/types";

import { FieldInclusionStrategy, FieldMappingConfig } from "./apiMapperTypes";

/**
 * Determines whether a field should be included based on the inclusion strategy
 */
const shouldIncludeField = (
  formData: FormData,
  sourceKey: keyof FormData,
  strategy: FieldInclusionStrategy
): boolean => {
  switch (strategy) {
    case "always":
      return true;
    case "truthy":
      return Boolean(formData[sourceKey]);
    case "exists":
      return sourceKey in formData;
    default:
      return false;
  }
};

/**
 * Generic function to map FormData to API format using field configuration
 * @param formData - Form data from FormEngine
 * @param fieldMappings - Array of field mapping configurations
 * @returns Object with mapped fields
 */
export const mapFormDataToApiFields = (
  formData: FormData,
  fieldMappings: FieldMappingConfig[]
): Record<string, unknown> => {
  const result: Record<string, unknown> = {};

  for (const config of fieldMappings) {
    const { sourceKey, targetKey = sourceKey, inclusion, transform } = config;

    if (shouldIncludeField(formData, sourceKey, inclusion)) {
      let value = formData[sourceKey];

      // Apply transformation if provided
      if (transform) {
        value = transform(value);
      }

      result[targetKey as string] = value;
    }
  }

  return result;
};

/**
 * Validates that all required fields are present in the form data
 * @param formData - Form data to validate
 * @param fieldMappings - Field mapping configurations
 * @returns Array of missing required field names
 */
export const validateRequiredFields = (
  formData: FormData,
  fieldMappings: FieldMappingConfig[]
): string[] => {
  const missingFields: string[] = [];

  for (const config of fieldMappings) {
    if (config.required && !formData[config.sourceKey]) {
      missingFields.push(config.sourceKey as string);
    }
  }

  return missingFields;
};

/**
 * Creates a mapping function with pre-configured field mappings
 * @param fieldMappings - Field mapping configurations to use
 * @returns Function that maps FormData to API fields
 */
export const createFormDataMapper = (fieldMappings: FieldMappingConfig[]) => {
  return (formData: FormData) =>
    mapFormDataToApiFields(formData, fieldMappings);
};
